import React, { useEffect, useState, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  ERROR_MESSAGE_MAPPER,
  ERROR_MEESAGE_PRIORITY,
  REALTIME_FEEDBACK_ERROR_WINDOW,
  REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY,
  DEFAULT_ERROR_LIST,
} from './constant';
import RealTimeFeedbackModal from './RealTimeFeedbackModal';
import { proctoringUBALogger } from '../../utils/trackingUtil';
import { PREDICTION_SUCCESS, TF_MODEL_LOADED } from '../../constants/Proctor';

function RealTimeProctoringFeedback({
  webcamRef,
  isFeedbackEnabled,
  externalAlertSignal,
}) {
  const [feedbackError, setFeedbackError] = useState({});
  const [
    isFeedbackIntervalInitiated,
    setIsFeedbackIntervalInitiated,
  ] = useState();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isFeedbackActive, setIsFeedbackActive] = useState(true);
  const workerRef = useRef(null);
  const { test, solutionId, webcamEnabled } = useSelector(
    state => ({
      test: state?.test,
      solutionId: state.testSolutionSet.id,
      webcamEnabled: state.proctor.webcamEnabled,
    }),
    shallowEqual
  );

  async function loadRealtimeFeedbackModel() {
    try {
      setIsFeedbackIntervalInitiated(true);
      proctoringUBALogger('info:TensorFlowModel', `TensorFlowModel loaded`);
    } catch (err) {
      proctoringUBALogger('error:TensorFlowModel', `failed to load model`);
    }
  }

  const captureImageData = () => {
    if (webcamRef?.current?.video) {
      const { video } = webcamRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      return imageData;
    }
    return null;
  };

  const realtimeFeedbackPrediction = () => {
    workerRef.current.postMessage({
      functionName: 'detectObjects',
      payload: captureImageData(),
    });
  };

  const realtimeFeedbackPredictionCallback = async feedbackPredictions => {
    const predictions = feedbackPredictions;
    let cachedRealtimeParameter = localStorage.getItem(
      REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY
    );
    if (cachedRealtimeParameter)
      cachedRealtimeParameter = JSON.parse(cachedRealtimeParameter);

    let errorList = { ...cachedRealtimeParameter };

    let personPredictionCounter = 0;
    predictions.forEach(prediction => {
      if (prediction.class === 'person') {
        personPredictionCounter += 1;
      }
    });

    if (errorList.noFace || errorList.noFace === 0)
      if (
        personPredictionCounter === 0 &&
        errorList.noFace !== REALTIME_FEEDBACK_ERROR_WINDOW
      ) {
        errorList.noFace += 1;
      } else if (errorList.noFace > 0) {
        errorList.noFace -= 1;
      }

    if (errorList.multiFace || errorList.multiFace === 0)
      if (
        personPredictionCounter > 1 &&
        errorList.multiFace !== REALTIME_FEEDBACK_ERROR_WINDOW
      ) {
        errorList.multiFace += 1;
      } else if (errorList.multiFace > 0) {
        errorList.multiFace -= 1;
      }
    const feedbackErrorList = { ...feedbackError };
    const errorParameter = { ...errorList };
    Object.keys(errorList).forEach(key => {
      if (
        errorList[key] === REALTIME_FEEDBACK_ERROR_WINDOW &&
        feedbackErrorList[ERROR_MEESAGE_PRIORITY[key]] !==
          ERROR_MESSAGE_MAPPER[key]
      ) {
        feedbackErrorList[ERROR_MEESAGE_PRIORITY[key]] =
          ERROR_MESSAGE_MAPPER[key];
        delete errorParameter[key];
      } else if (errorList[key] === 0) {
        delete feedbackErrorList[ERROR_MEESAGE_PRIORITY[key]];
      }
    });
    if (Object.keys(feedbackErrorList).length > 0) {
      setShowFeedbackModal(true);
      errorList = { ...errorParameter };
      if (Object.keys(errorList).length === 0) {
        setIsFeedbackActive(false);
      }
    }
    localStorage.setItem(
      REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY,
      JSON.stringify({ ...errorParameter })
    );
    setFeedbackError({ ...feedbackErrorList });
  };

  useEffect(() => {
    if (window.Worker) {
      const workerInstance = new Worker(
        `${process.env.PUBLIC_URL}/testWorker.js`
      );
      workerRef.current = workerInstance;

      workerInstance.onmessage = event => {
        if (event.data === TF_MODEL_LOADED) {
          loadRealtimeFeedbackModel();
        }
        if (event.data?.action === PREDICTION_SUCCESS) {
          realtimeFeedbackPredictionCallback(event.data.predictions);
        }
      };
    }
  }, []);

  // External trigger: show the same realtime feedback popup when parent explicitly signals.
  useEffect(() => {
    if (!externalAlertSignal) return;
    setShowFeedbackModal(true);
    proctoringUBALogger(
      'warning:realtime-feedback',
      'Realtime feedback modal triggered due to webcam issue (consecutive)'
    );
  }, [externalAlertSignal]);

  useEffect(() => {
    let realtimeFeedbackInterval;
    if (
      isFeedbackIntervalInitiated &&
      !test.is_expired &&
      solutionId &&
      !realtimeFeedbackInterval &&
      webcamEnabled &&
      isFeedbackEnabled &&
      isFeedbackActive &&
      webcamRef.current?.stream?.active // To check current.stream.active is defined
    ) {
      let cachedRealtimeParameter = localStorage.getItem(
        REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY
      );
      cachedRealtimeParameter = cachedRealtimeParameter
        ? JSON.parse(cachedRealtimeParameter)
        : {};
      if (Object.keys(cachedRealtimeParameter).length === 0) {
        localStorage.setItem(
          REALTIME_ALERT_PARAMETERS_LOCALSTORAGE_KEY,
          JSON.stringify(DEFAULT_ERROR_LIST)
        );
      }

      realtimeFeedbackInterval = setInterval(
        () => realtimeFeedbackPrediction(),
        1000
      );
    }
    return () => {
      clearInterval(realtimeFeedbackInterval);
    };
  }, [
    isFeedbackIntervalInitiated,
    test.is_expired,
    solutionId,
    webcamRef.current?.stream?.active,
    webcamEnabled,
    isFeedbackActive,
  ]);

  return (
    <RealTimeFeedbackModal
      showModal={
        showFeedbackModal && (isFeedbackEnabled || !!externalAlertSignal)
      }
      setShowModal={setShowFeedbackModal}
      productType={test.productType}
    />
  );
}

export default RealTimeProctoringFeedback;
